import React, {
  ReactNode,
  RefObject,
  useRef,
  useState,
  FocusEvent,
  KeyboardEvent,
  useEffect,
} from 'react';
import Button from 'pages/Dashboard/components/Button';
import InfoIcon from 'core/icons/info';
import Divider from '@mui/material/Divider';
import {
  formatMoney,
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import CurrencyTextField from 'core/components/CurrencyTextField';
import {
  CellContext,
} from '@tanstack/react-table';
import {
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import {
  EditableTableMeta,
} from 'core/components/VirtualTable/types';
import clsx from 'clsx';
import AmountCell from 'pages/Dashboard/pages/Billing/Payments/AmountCell';
import isNil from 'lodash/isNil';
import {
  EncounterWithPayment,
} from 'views/EMR/Billing/AdjustPayment';

type InputSuggestionProps = {
  value?: string | number | null;
  onApply?: () => void;
  title?: string;
};

type SuggestionTooltipProps = InputSuggestionProps & {
  children: ReactNode;
  open?: boolean;
};

const underlineClassName = '!underline !underline-offset-2 !decoration-dashed !decoration-gray-400';

type EditableCurrencyProps = {
  value?: string | number | null;
  onChange: (value: string | number | null | undefined) => void;
  onFocus?: () => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  focused?: boolean;
  suggestionIndicator?: boolean;
  showNegative?: boolean;
  error?: boolean;
};

const insuranceWriteoffSuggestionTitle = 'Billed Amount - Copay - Deductible - Coinsurance - Cash/Non Covered - Insurance Payment';
const moveToCreditSuggestionTitle = 'Patient Paid - Patient Responsibility';
const indicatorClassName = 'absolute w-0 h-0 border-t-solid border-r-solid border-r-transparent border-t-[10px] border-r-[10px] -top-0.5 left-0 !m-0';

function EditableCurrency({
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  focused,
  suggestionIndicator,
  showNegative = true,
  error,
}: EditableCurrencyProps) {
  const inputRef = useRef<HTMLInputElement>(null!);
  const isNegative = Number(value) < 0;
  const showNotSet = isNil(value) && !focused;
  return (
    <div className="flex items-center w-full -mx-1 flex-1 editable-currency-input-container">
      <div className={clsx(
        'flex justify-between items-center w-full flex-1 relative',
        { 'px-1': !focused },
      )}
      >

        {!focused && (
          <>
            {isNegative && showNegative && (<div className={clsx(indicatorClassName, ' border-t-red-500')} />)}
            {error && (<div className={clsx(indicatorClassName, ' border-t-red-500')} />)}
            {!error && suggestionIndicator && (<div className={clsx(indicatorClassName, ' border-t-blue-500')} />)}
          </>
        )}
        <CurrencyTextField
          inputRef={inputRef as RefObject<HTMLInputElement>}
          allowNegative
          nullIfEmpty
          value={value ?? ''}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={clsx('hover:!border-gray-300', {
            'negative-input': isNegative && !focused && showNegative,
            '!w-0': showNotSet,
            'text-red-500': error,
            [underlineClassName]: !focused,
          })}
        />
        <Button
          variant="text"
          className={clsx('!p-0', {
            '!hidden': focused,
            '!max-w-full w-full flex !justify-between': showNotSet,
          })}
          onFocus={() => { inputRef?.current?.focus?.(); }}
        >
          {showNotSet && <div className={clsx('not-set-placeholder font-semibold whitespace-nowrap', underlineClassName)}>Not set</div>}
        </Button>
      </div>
    </div>
  );
}

function InputSuggestion({ value, onApply, title }: InputSuggestionProps) {
  const isTitleVisible = notEmptyString(title);
  return (
    <div className={clsx('flex flex-col w-80 gap-1', {
      '!w-40': !isTitleVisible,
    })}
    >
      {notEmptyString(title) && (
        <>
          <div className="flex items-start gap-1">
            <InfoIcon className="!w-4 !h-4" />
            {title}
          </div>
          <Divider />
        </>
      )}
      <div className="flex pb-1 gap-1 justify-between flex-col">
        <div>
          <strong className="font-semibold">{formatMoney((value as number) ?? 0)}</strong>
          {' '}
          Suggested
        </div>
        <Button
          variant="outlined"
          color="primary"
          className="text-blue-500 border-blue-500 px-2 !h-6"
          onMouseDown={() => { onApply?.(); }}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

function SuggestionTooltip({
  children,
  open,
  ...props
}: SuggestionTooltipProps) {
  return (
    <ToolTip
      title={<InputSuggestion {...props} />}
      placement="top-start"
      open={open}
      isInteractive
      popperClassName="!z-[2000]"
    >
      <div className="w-full flex flex-1">
        {children}
      </div>
    </ToolTip>
  );
}

export function EditableInsuranceWriteOff({
  row,
  column,
  table,
  getValue,
}: CellContext<ServiceAllocation, unknown>) {
  const [isVisibleSuggestion, setVisibleSuggestion] = useState<boolean>(true);
  const { updateData } = table.options.meta as EditableTableMeta<ServiceAllocation>;

  const initialValue = getValue<number | string | null | undefined>();
  const [value, setValue] = useState<number | string | null | undefined>(initialValue);
  const [focused, setFocused] = useState(false);

  const suggestedValue = row.original?.suggestedInsuranceWriteoff;
  const hasSuggestion = isNilOrZero(suggestedValue) || suggestedValue === Number(value);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (focused) {
      setVisibleSuggestion(true);
    }
  }, [focused]);

  const suggestionEnabled = !hasSuggestion && focused && isVisibleSuggestion;

  return (
    <SuggestionTooltip
      value={suggestedValue}
      title={insuranceWriteoffSuggestionTitle}
      onApply={() => {
        setValue(row.original?.suggestedInsuranceWriteoff);
      }}
      open={suggestionEnabled}
    >
      <EditableCurrency
        key={`${row?.index}_${column?.id}_${initialValue ?? ''}`} // to force render and adapt aplied value
        value={value}
        onChange={(value) => { setValue(value); }}
        onFocus={() => { setFocused(true); }}
        onBlur={(e) => {
          e.stopPropagation();
          setFocused(false);
          if (value !== initialValue) {
            updateData?.(row.index, column.id, value, row?.original);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && suggestionEnabled) {
            setValue(row.original?.suggestedInsuranceWriteoff);
          }

          if (e.key === 'Escape') {
            setVisibleSuggestion(false);
          }
        }}
        focused={focused}
        suggestionIndicator={!hasSuggestion}
      />
    </SuggestionTooltip>
  );
}

export function EditableCurrencyCell({
  row,
  column,
  table,
  getValue,
}: CellContext<ServiceAllocation, unknown>) {
  const { updateData } = table.options.meta as EditableTableMeta<ServiceAllocation>;
  const initialValue = getValue<number | string | null | undefined>();
  const [value, setValue] = useState<number | string | null | undefined>(initialValue);
  const [focused, setFocused] = useState<boolean>(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <EditableCurrency
      value={value}
      onChange={(value) => { setValue(value); }}
      onFocus={() => { setFocused(true); }}
      onBlur={(e) => {
        e.stopPropagation();
        setFocused(false);
        if (value !== initialValue) {
          updateData?.(row.index, column.id, value, row?.original);
        }
      }}
      focused={focused}
    />
  );
}

export function MoveToCreditCurrencyCell({
  row,
  column,
  table,
  getValue,
}: CellContext<EncounterWithPayment, unknown>) {
  const { updateData } = table.options.meta as EditableTableMeta<EncounterWithPayment>;
  const initialValue = getValue<number | string | null | undefined>();
  const [value, setValue] = useState<number | string | null | undefined>(initialValue);
  const [focused, setFocused] = useState<boolean>(false);
  const [isVisibleSuggestion, setVisibleSuggestion] = useState<boolean>(true);
  const suggestedValue = row.original?.suggestedAmount ?? 0;
  const suggestionEnabled = !isNilOrZero(suggestedValue)
    && suggestedValue !== Number(value)
    && suggestedValue > 0
    && focused
    && isVisibleSuggestion;

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (focused) {
      setVisibleSuggestion(true);
    }
  }, [focused]);

  return (
    <SuggestionTooltip
      value={suggestedValue}
      title={row?.original?.isCredit ? undefined : moveToCreditSuggestionTitle}
      onApply={() => {
        setValue(suggestedValue);
      }}
      open={suggestionEnabled}
    >
      <EditableCurrency
        value={value}
        onChange={(value) => { setValue(value); }}
        onFocus={() => { setFocused(true); }}
        onBlur={(e) => {
          e.stopPropagation();
          setFocused(false);
          updateData?.(row.index, column.id, value, row?.original);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && suggestionEnabled) {
            setValue(suggestedValue);
          }

          if (e.key === 'Escape') {
            setVisibleSuggestion(false);
          }
        }}
        focused={focused}
        suggestionIndicator={!isNilOrZero(suggestedValue) && suggestedValue !== Number(value)}
        error={(!isNil(value) && Number(value) <= 0)
          || (Number(value) ?? 0) > (row.original.totalPatientPaid ?? 0)}
      />
    </SuggestionTooltip>
  );
}

export function ReadOnlyCurrencyCell({
  column,
  getValue,
}: CellContext<ServiceAllocation, unknown>) {
  const value = getValue<number | null | undefined>();

  return (
    <div className="flex flex-1 items-center">
      <AmountCell<ServiceAllocation>
        value={value}
        columnDef={column.columnDef}
        showZero
        showNegative={false}
      />
    </div>
  );
}
